import Swiper from 'swiper';
import { Keyboard, Navigation } from 'swiper/modules';

export function contentSingleProduct() {
  new Swiper('article.product .swiper', {
    speed: 500,
    loop: true,
    spaceBetween: 8,
    slidesPerView: 1.1,
    loopAdditionalSlides: 1,
    navigation: {
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev',
    },
    breakpoints: {
      1024: {
        spaceBetween: 24,
        slidesPerView: 'auto',
      },
    },
    keyboard: {
      enabled: true,
    },
    modules: [Navigation, Keyboard],
  });
  if (document.querySelector('.component.configurator')) {
    configurator();
  }

  // Add lightbox functionality for mobile devices
  setupImageLightbox();
}

function setupImageLightbox() {
  // Only apply lightbox on screens below 1024px
  const isSmallScreen = window.innerWidth < 1024;
  if (!isSmallScreen) return;

  const images = document.querySelectorAll('.swiper-slide img');
  if (!images.length) return;

  // Create lightbox elements if they don't exist yet
  let lightbox = document.getElementById('image-lightbox');
  if (!lightbox) {
    lightbox = document.createElement('div');
    lightbox.id = 'image-lightbox';
    lightbox.className =
      'fixed inset-0 z-50 flex items-center justify-center bg-black/75 transition-opacity opacity-0 pointer-events-none';

    const lightboxContent = document.createElement('div');
    lightboxContent.className = 'max-w-full max-h-full p-5';

    const lightboxImage = document.createElement('img');
    lightboxImage.id = 'lightbox-image';
    lightboxImage.alt = 'Lightbox';
    lightboxImage.className =
      'max-h-screen max-w-full object-contain rounded-lg';

    const closeButton = document.createElement('button');
    closeButton.className =
      'absolute top-4 right-4 size-10 flex items-center justify-center cursor-pointer bg-white rounded-full text-black';
    closeButton.setAttribute('aria-label', 'Close lightbox');
    closeButton.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" class="size-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>';

    closeButton.addEventListener('click', () => {
      closeLightbox(lightbox);
    });

    lightboxContent.appendChild(lightboxImage);
    lightboxContent.appendChild(closeButton);
    lightbox.appendChild(lightboxContent);
    document.body.appendChild(lightbox);

    // Close on background click
    lightbox.addEventListener('click', (e) => {
      if (e.target === lightbox) {
        closeLightbox(lightbox);
      }
    });

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (
        e.key === 'Escape' &&
        !lightbox.classList.contains('pointer-events-none')
      ) {
        closeLightbox(lightbox);
      }
    });
  }

  // Add click handler to each image
  images.forEach((img) => {
    img.style.cursor = 'pointer';
    img.addEventListener('click', () => {
      openLightbox(lightbox, img.src, img.alt);
    });
  });
}

function openLightbox(lightbox, imageSrc, imageAlt) {
  // Set image source
  const lightboxImage = document.getElementById('lightbox-image');
  lightboxImage.src = imageSrc;
  lightboxImage.alt = imageAlt;

  // Prevent body scroll
  document.body.style.overflow = 'hidden';

  // Show lightbox with animation
  lightbox.classList.remove('pointer-events-none', 'opacity-0');

  // Add listener to recalculate on resize
  window.addEventListener('resize', handleResize);
}

function closeLightbox(lightbox) {
  // Hide lightbox with animation
  lightbox.classList.add('opacity-0');

  // Wait for animation to complete
  setTimeout(() => {
    lightbox.classList.add('pointer-events-none');
    // Allow body scroll again
    document.body.style.overflow = '';
  }, 300);

  // Remove resize listener
  window.removeEventListener('resize', handleResize);
}

function handleResize() {
  const isSmallScreen = window.innerWidth < 1024;
  const lightbox = document.getElementById('image-lightbox');

  if (
    !isSmallScreen &&
    lightbox &&
    !lightbox.classList.contains('pointer-events-none')
  ) {
    // Close lightbox if screen becomes large while lightbox is open
    closeLightbox(lightbox);
  }
}

function configurator() {
  const configuratorElement = document.querySelector('.component.configurator');

  // Get the product ID from the data attribute
  const productId = configuratorElement.dataset.productId;
  if (!productId) {
    console.error('No product ID found for configurator');
    return;
  }

  // Config URL from global object
  const configUrl = window.js_localizations?.configurations_url;
  if (!configUrl) {
    console.error('Configuration URL not found in window.js_localizations');
    return;
  }

  // Create attributes state tracking
  let attributeSelections = {};
  let productData = null;
  let attributeOptions = null;

  // Store the attribute order for determining availability
  let attributeOrder = [];

  // Elements for updating
  const orderButton = document.getElementById('order-button');
  const configImage = document.getElementById('config-image');
  const configTitle = document.getElementById('config-title');
  const configDescription = document.getElementById('config-description');
  const priceElement = document.getElementById('config-price');

  // Initialize the product data
  fetchProductData(productId);

  // Main function to fetch the product data
  async function fetchProductData(productId) {
    try {
      // Construct URL with required GET parameters
      const url = `${configUrl}?product_id=${productId}&show_full_variation_info=1`;

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      // Process the product data
      if (data.products && data.products[productId]) {
        productData = data.products[productId];
        attributeOptions = data.attribute_options;

        // Store the attribute order
        attributeOrder = productData.attributes;

        setupAttributeHandlers(productData, attributeOptions);

        // Initialize attribute selections with the first variation's values
        if (productData.variations && productData.variations.length > 0) {
          const firstVariation = productData.variations[0];
          productData.attributes.forEach((attributeId) => {
            attributeSelections[attributeId] = firstVariation[attributeId];
          });

          // After initializing selections, update available options
          updateAvailableOptions();
        }
      }

      if (response.ok) {
        document
          .getElementById('open-modal-configurator')
          .removeAttribute('disabled');
      }
    } catch (error) {
      console.error('Error fetching product data:', error);
    }
  }

  // Setup the attribute option handlers
  function setupAttributeHandlers(product, attributeOptions) {
    const attributeElements =
      configuratorElement.querySelectorAll('.attribute');

    attributeElements.forEach((attributeElement) => {
      const attributeId = attributeElement.dataset.attributeId;
      const optionButtons = attributeElement.querySelectorAll('.option');

      optionButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const optionId = button.dataset.optionId;

          // Update aria-pressed for this attribute's buttons
          optionButtons.forEach((b) => b.setAttribute('aria-pressed', 'false'));
          button.setAttribute('aria-pressed', 'true');

          // Update selection
          attributeSelections[attributeId] = optionId;

          // Update available options for subsequent attributes
          updateAvailableOptions();

          // Update display with matching variation
          updateProductDisplay(product, attributeOptions);

          // Update order URL
          updateOrderUrl();
        });
      });
    });
  }

  // Update available options based on current selections
  function updateAvailableOptions() {
    if (!productData || !attributeOptions) return;

    const attributeElements =
      configuratorElement.querySelectorAll('.attribute');

    attributeElements.forEach((attributeElement) => {
      const attributeId = attributeElement.dataset.attributeId;
      const attributeIndex = attributeOrder.indexOf(parseInt(attributeId));
      const optionButtons = attributeElement.querySelectorAll('.option');

      // Only process options for this attribute and subsequent attributes
      // Skip if this is the first attribute (always all available)
      if (attributeIndex <= 0) return;

      // Get all previous attribute IDs in the order
      const previousAttributeIds = attributeOrder.slice(0, attributeIndex);

      optionButtons.forEach((button) => {
        const optionId = button.dataset.optionId;

        // Check if this option is available given the current selections
        const isAvailable = isOptionAvailable(
          parseInt(attributeId),
          parseInt(optionId),
          previousAttributeIds,
        );

        if (isAvailable) {
          button.removeAttribute('disabled');
          button.classList.remove('opacity-50');

          // Remove sold out line if it exists
          const existingSoldOutLine = button.querySelector('.sold-out-line');
          if (existingSoldOutLine) {
            button.removeChild(existingSoldOutLine);
          }
        } else {
          button.setAttribute('disabled', 'true');
          button.classList.add('opacity-50');

          // Add sold out line if it doesn't exist
          if (!button.querySelector('.sold-out-line')) {
            // Create diagonal sold out line
            const soldOutLine = document.createElement('div');
            soldOutLine.className =
              'sold-out-line absolute inset-0 pointer-events-none z-10';
            soldOutLine.innerHTML = `
              <svg class="absolute inset-0 w-full h-full opacity-50" preserveAspectRatio="none">
                <line
                  x1="0"
                  y1="100%"
                  x2="100%"
                  y2="0"
                  stroke="red"
                  stroke-width="3"
                />
              </svg>
            `;

            // Add position relative if not already present
            if (!button.classList.contains('relative')) {
              button.classList.add('relative');
            }

            button.appendChild(soldOutLine);
          }

          // If this option is currently selected but not available,
          // find the first available option and select it
          if (button.getAttribute('aria-pressed') === 'true') {
            const firstAvailableOption = Array.from(optionButtons).find(
              (opt) => {
                const optId = opt.dataset.optionId;
                return isOptionAvailable(
                  parseInt(attributeId),
                  parseInt(optId),
                  previousAttributeIds,
                );
              },
            );

            if (firstAvailableOption) {
              // Update selection to the first available option
              optionButtons.forEach((b) =>
                b.setAttribute('aria-pressed', 'false'),
              );
              firstAvailableOption.setAttribute('aria-pressed', 'true');
              attributeSelections[attributeId] =
                firstAvailableOption.dataset.optionId;
            }
          }
        }
      });
    });
  }

  // Check if an option is available given previous selections
  function isOptionAvailable(attributeId, optionId, previousAttributeIds) {
    // Get all variations that match our current selections for previous attributes
    const matchingVariations = productData.variations.filter((variation) => {
      return previousAttributeIds.every((prevAttrId) => {
        return variation[prevAttrId] == attributeSelections[prevAttrId];
      });
    });

    // Check if any of these variations include our option
    return matchingVariations.some(
      (variation) => variation[attributeId] == optionId,
    );
  }

  // Update product display based on current selections
  function updateProductDisplay(product, attributeOptions) {
    // Find matching variation
    const matchingVariation = findMatchingVariation(
      product.variations,
      attributeSelections,
    );

    if (matchingVariation) {
      // Update image
      if (configImage && matchingVariation.image) {
        // Change source element of parent <picture> element in case WebP Express is being used
        if (configImage.previousSibling.nodeName.toLowerCase() == 'source') {
          configImage.previousSibling.srcset = matchingVariation.image;
        }

        configImage.src = matchingVariation.image;
        configImage.alt = matchingVariation.title || '';
      }

      // Update title
      if (configTitle) {
        configTitle.textContent = matchingVariation.title || '';
      }

      // Update description
      if (configDescription) {
        configDescription.innerHTML = matchingVariation.description || '';
      }

      // Update price
      if (
        priceElement &&
        (matchingVariation.price || matchingVariation.custom)
      ) {
        if (matchingVariation.custom == true) {
          priceElement.textContent = window.js_localizations.custom_label;
        } else {
          priceElement.textContent = formatPrice(matchingVariation.price);
        }
      }
    }
  }

  function formatPrice(price) {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
    })
      .format(price)
      .replace(',00', ',-');
  }

  // Find the variation that matches current selections
  function findMatchingVariation(variations, selections) {
    return variations.find((variation) => {
      return Object.keys(selections).every((attributeId) => {
        return variation[attributeId] == selections[attributeId];
      });
    });
  }

  // Update the order URL with current attribute selections
  function updateOrderUrl() {
    if (!orderButton) return;

    const baseUrl = orderButton.dataset.orderUrl;
    if (!baseUrl) return;

    let queryString = '';

    Object.entries(attributeSelections).forEach(
      ([attributeId, optionId], index) => {
        queryString += `&attribute_${attributeId}=${optionId}`;
      },
    );

    orderButton.href = baseUrl + queryString;
  }
}
